import moment from 'moment'
import * as React from 'react'
import FlexView from '@/modules/shared/components/FlexView'

import { DriverCalendarModel } from 'app/models/calendar/driverCalendarModel'
import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'

import { CalendarOption } from 'app/constants/calendarOptions'
import { DATE_FORMAT } from 'app/constants/date'

import { CalendarDayItem, CalendarMenuAction } from './CalendarDayItem'
import { DayContextMenu } from './CalendarDayContextMenu'
import { CalendarMonths } from './CalendarMonths'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import { ActivitiesEmptyState } from '@/modules/shared/components/ActivitiesEmptyState'
import { CalendarCellsPlaceholder } from './CalendarCellsPlaceholder'
import { CalendarHeaderItem } from './CalendarHeaderItem'
import CalendarDayInfoPopup from './popup/CalendarDayInfoPopup'
import { createPortal } from 'react-dom'
import CertificateModal from '../../../modals/CertificateModal'
import {
  CALENDAR_COLUMNS,
  CALENDAR_DAY_HEIGHT as HEIGHT,
  CALENDAR_ROWS,
  MONTH_COLUMN_WIDTH,
  WEEK_DAY_HEADER_HEIGHT,
  getDayWidth,
} from './calendarLayout'

export type CalendarProps = {
  companyId: string
  driverId?: number
  calendar: DriverCalendarModel | null
  selectedOptions?: Array<CalendarOption>
  isLoading: boolean
  onSelected?: (day: DriverCalendarDayModel) => void
  onCalendarAction?: (
    day: DriverCalendarDayModel,
    action: CalendarMenuAction,
    data?: any,
  ) => void
  selectedDays?: Array<DriverCalendarDayModel>
  onMultiSelected?: (days: Array<DriverCalendarDayModel>) => void
  changedDay?: DriverCalendarDayModel
  multiSelectMode?: boolean
  showViolations?: boolean
  refreshCalendar?: () => Promise<void>
  showTrips: boolean
}

const YearlyActivityCalendar: React.FC<CalendarProps> = (props) => {
  const {
    companyId,
    driverId,
    calendar,
    selectedOptions,
    onSelected,
    onCalendarAction,
    selectedDays: propsSelectedDays,
    onMultiSelected,
    changedDay,
    multiSelectMode,
    showViolations,
    refreshCalendar,
    isLoading,
    showTrips
  } = props
  const { t } = useLocalize()
  const [isCertificateModalOpen, setIsCertificateModalOpen] =
    React.useState(false) 
  const [certificateDates, setCertificateDates] = React.useState<
    Array<Date | string>
  >([])

  const [selectedDay, setSelectedDay] =
    React.useState<DriverCalendarDayModel | null>(null)
  const [selectedDays, setSelectedDays] = React.useState<
    Array<DriverCalendarDayModel>
  >([])
  const [selectedDates, setSelectedDates] = React.useState<Array<Date>>([])
  const [days, setDays] = React.useState<Array<Array<DriverCalendarDayModel>>>(
    calendar != null ? calendar?.dayActivities : [],
  )

  const isDraggingRef = React.useRef(false)
  const dragStartDayRef = React.useRef<string | null>(null)
  const dragRangeRef = React.useRef<[number, number] | null>(null)
  const [popupDay, setPopupDay] = React.useState<DriverCalendarDayModel | null>(
    null,
  )
  const [popupAnchor, setPopupAnchor] = React.useState<{
    top: number
    left: number
  } | null>(null)

  const [contextMenu, setContextMenu] = React.useState<{
    day: DriverCalendarDayModel
    anchor: { x: number; y: number }
    isOpen: boolean
  } | null>(null)

  const handleContextMenuRequest = React.useCallback(
    (day: DriverCalendarDayModel, anchor: { x: number; y: number }) => {
      setContextMenu({ day, anchor, isOpen: true })
    },
    [],
  )

  const handleContextMenuClose = React.useCallback(() => {
    setContextMenu((prev) => (prev ? { ...prev, isOpen: false } : null))
  }, [])

  const popupRef = React.useRef<HTMLDivElement>(null)
  const calendarRef = React.useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  React.useEffect(() => {
    const el = calendarRef.current
    if (!el) return undefined
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width)
    })
    observer.observe(el)
    setContainerWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    if (!popupDay || !popupAnchor) return
    const handleClickOutside = (e: MouseEvent) => {
      if (e.button !== 0) return
      const target = e.target as Node
      const isInsidePopup = popupRef.current?.contains(target)
      if (isInsidePopup) return

      const el = e.target as HTMLElement
      const dayCell = el.closest('.day-item, .day-item-multi-selection')
      if (dayCell) return

      setPopupDay(null)
      setPopupAnchor(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [popupDay, popupAnchor])

  const flatDays = React.useMemo(() => {
    if (!days) return []
    return days
      .flat()
      .filter(
        (d): d is DriverCalendarDayModel => d != null && d.dayNumber !== 0,
      )
  }, [days])

  const dayIndexMap = React.useMemo(() => {
    const map = new Map<string, number>()
    flatDays.forEach((d, i) => {
      map.set(moment(d.activityDayLocalTimeDate).format(DATE_FORMAT), i)
    })
    return map
  }, [flatDays])

  React.useEffect(() => {
    const handleMouseUp = () => {
      if (isDraggingRef.current && dragRangeRef.current) {
        const [min, max] = dragRangeRef.current
        const rangeDays = flatDays.slice(min, max + 1)
        const rangeDates = rangeDays.map((d) =>
          moment(d.activityDayLocalTimeDate).toDate(),
        )
        setSelectedDays(rangeDays)
        setSelectedDates(rangeDates)
        onMultiSelected?.(rangeDays)
      }
      isDraggingRef.current = false
      dragRangeRef.current = null
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [flatDays, onMultiSelected])

  const handleDayMouseDown = React.useCallback(
    (day: DriverCalendarDayModel, e: React.MouseEvent) => {
      if (e.button !== 0) return
      isDraggingRef.current = true
      dragStartDayRef.current = moment(day.activityDayLocalTimeDate).format(
        DATE_FORMAT,
      )
    },
    [],
  )

  const handleDayMouseEnter = React.useCallback(
    (day: DriverCalendarDayModel) => {
      if (!isDraggingRef.current || !dragStartDayRef.current) return
      const startIdx = dayIndexMap.get(dragStartDayRef.current)
      const endIdx = dayIndexMap.get(
        moment(day.activityDayLocalTimeDate).format(DATE_FORMAT),
      )
      if (startIdx == null || endIdx == null) return
      const min = Math.min(startIdx, endIdx)
      const max = Math.max(startIdx, endIdx)
      dragRangeRef.current = [min, max]

      const newSet = new Set<string>()
      for (let i = min; i <= max; i++) {
        newSet.add(
          moment(flatDays[i].activityDayLocalTimeDate).format(DATE_FORMAT),
        )
      }
      setSelectedDaysSet(newSet)
    },
    [flatDays, dayIndexMap],
  )

  React.useEffect(() => {
    if (calendar != null) {
      setDays(calendar?.dayActivities)
    }
  }, [calendar])

  React.useEffect(() => {
    if (days != null && changedDay != null) {
      setDays((prevDays) =>
        prevDays.map((r) => {
          return r.map((c) => {
            let day = c
            if (
              c != null &&
              moment(c.activityDayLocalTimeDate).format(DATE_FORMAT) ==
                moment(changedDay.activityDayLocalTimeDate).format(DATE_FORMAT)
            ) {
              day = {
                ...c,
                info: changedDay.info,
                dayType: changedDay.dayType,
              }
            }
            return day
          })
        }),
      )
    }
  }, [changedDay])

  React.useEffect(() => {
    if (propsSelectedDays != null) {
      setSelectedDays(propsSelectedDays)
    }
  }, [propsSelectedDays])

  const [selectedDaysSet, setSelectedDaysSet] = React.useState<Set<string>>(
    () => new Set(),
  )

  React.useEffect(() => {
    if (isDraggingRef.current) return
    setSelectedDaysSet(
      new Set(
        selectedDays.map((d) =>
          moment(d.activityDayLocalTimeDate).format(DATE_FORMAT),
        ),
      ),
    )
  }, [selectedDays])

  const dateRange = (startDate, endDate) => {
    let currentTime = moment(startDate)
    const endTime = moment(endDate)

    const result = []
    while (currentTime <= endTime) {
      result.push(currentTime.toDate())
      currentTime = currentTime.clone().add(1, 'days')
    }

    return result
  }

  const prepareHeader = (): Array<number> => {
    const header: Array<number> = []
    const daysCount = days?.[0]?.length || CALENDAR_COLUMNS

    for (let i = 0; i < daysCount; i++) {
      header.push(i % 7)
    }

    return header
  }

  const renderWeekDayHeaders = (width: number) => {
    const header = prepareHeader()

    return (
      <FlexView
        height={WEEK_DAY_HEADER_HEIGHT}
        hAlignContent="left"
        vAlignContent="center"
        style={{
          border: 'solid',
          borderWidth: 0,
        }}
      >
        {header.map((item, index) => {
          return (
            <CalendarHeaderItem
              key={index}
              dayOfWeek={item}
              height={32}
              width={width}
              translate={t}
            />
          )
        })}
      </FlexView>
    )
  }

  const renderMonths = (height: number, width: number) => {
    return (
      <div className="st-sticky tw-z-20">
        <div
          style={{
            width: width,
            height: WEEK_DAY_HEADER_HEIGHT,
            borderStyle: 'solid',
            borderWidth: 0,
            borderRadius: 0,
            background: '#fffff',
          }}
        />
        <CalendarMonths height={height} width={width} translate={t} />
      </div>
    )
  }

  const handleDaySelection = React.useCallback(
    (daySelected: DriverCalendarDayModel, e: MouseEvent) => {
      if ((e.target as any).className.indexOf('react-contextmenu-item') != -1) {
        return
      }

      const isRightClick = e.button === 2
      const isLeftClick = e.button === 0

      // Right click: only select the day, no popup
      if (isRightClick) {
        setPopupDay(null)
        setPopupAnchor(null)
        if (
          !selectedDaysSet.has(
            moment(daySelected.activityDayLocalTimeDate).format(DATE_FORMAT),
          )
        ) {
          setSelectedDay(daySelected)
          setSelectedDays([daySelected])
          setSelectedDates([
            moment(daySelected.activityDayLocalTimeDate).toDate(),
          ])
          onSelected && onSelected(daySelected)
          onMultiSelected && onMultiSelected([daySelected])
        }
        return
      }

      if (!isLeftClick) return

      const isMultiSelectKey = e.metaKey || e.ctrlKey

      if (isMultiSelectKey || e.shiftKey) {
        let newSelectedDates = []

        if (e.shiftKey) {
          if (selectedDays.length > 0) {
            const firstDay = new Date(Math.min.apply(null, selectedDates))
            const selectedDate = moment(
              daySelected.activityDayLocalTimeDate,
            ).toDate()

            if (selectedDate > firstDay) {
              const range = dateRange(firstDay, selectedDate)
              newSelectedDates = [
                ...selectedDates.filter(
                  (sd) =>
                    !range.some(
                      (r) =>
                        moment(r).format(DATE_FORMAT) ==
                        moment(sd).format(DATE_FORMAT),
                    ),
                ),
                ...range,
              ]
            } else {
              const range = dateRange(selectedDate, firstDay)
              newSelectedDates = [
                ...selectedDates.filter(
                  (sd) =>
                    !range.some(
                      (r) =>
                        moment(r).format(DATE_FORMAT) ==
                        moment(sd).format(DATE_FORMAT),
                    ),
                ),
                ...range,
              ]
            }
          } else {
            newSelectedDates = [
              moment(daySelected.activityDayLocalTimeDate).toDate(),
            ]
          }
        } else {
          const dayDateFormatted = moment(
            daySelected.activityDayLocalTimeDate,
          ).format(DATE_FORMAT)

          if (!selectedDaysSet.has(dayDateFormatted)) {
            newSelectedDates = [
              ...selectedDates,
              moment(daySelected.activityDayLocalTimeDate).toDate(),
            ]
          } else {
            newSelectedDates = selectedDates.filter(
              (d) => moment(d).format(DATE_FORMAT) != dayDateFormatted,
            )
          }
        }

        const selectedDatesSet = new Set(
          newSelectedDates.map((d) => moment(d).format(DATE_FORMAT)),
        )

        const allSelectedDays: DriverCalendarDayModel[] = []
        days.forEach((rows) => {
          rows.forEach((d) => {
            if (
              selectedDatesSet.has(
                moment(d.activityDayLocalTimeDate).format(DATE_FORMAT),
              )
            ) {
              allSelectedDays.push(d)
            }
          })
        })

        setSelectedDates(newSelectedDates)
        setSelectedDays(allSelectedDays)
        setPopupDay(null)
        setPopupAnchor(null)

        onSelected && onSelected(daySelected)
        onMultiSelected && onMultiSelected(allSelectedDays)
      } else {
        const newDates = [moment(daySelected.activityDayLocalTimeDate).toDate()]
        const isSameDay =
          selectedDay &&
          moment(selectedDay.activityDayLocalTimeDate).format(DATE_FORMAT) ===
            moment(daySelected.activityDayLocalTimeDate).format(DATE_FORMAT)

        const isSamePopupDay =
          popupDay &&
          moment(popupDay.activityDayLocalTimeDate).format(DATE_FORMAT) ===
            moment(daySelected.activityDayLocalTimeDate).format(DATE_FORMAT)

        if (isSameDay) {
          setSelectedDay(null)
          setSelectedDays([])
          setSelectedDates([])
        } else {
          setSelectedDay(daySelected)
          setSelectedDays([daySelected])
          setSelectedDates(newDates)
        }

        if (isSamePopupDay) {
          setPopupDay(null)
          setPopupAnchor(null)
        } else {
          setPopupDay(daySelected)
          const target = e.currentTarget as HTMLElement
          const rect = target.getBoundingClientRect()
          const popupWidth = 450
          const popupHeight = 600
          let left = rect.right + 8
          let top = rect.top
          if (left + popupWidth > window.innerWidth) {
            left = rect.left - popupWidth - 8
          }
          if (top + popupHeight > window.innerHeight) {
            top = Math.max(8, window.innerHeight - popupHeight - 16)
          }
          setPopupAnchor({ top, left })
        }

        onSelected && onSelected(daySelected)
        onMultiSelected && onMultiSelected(isSameDay ? [] : [daySelected])
      }
    },
    [
      selectedDaysSet,
      selectedDates,
      selectedDays,
      days,
      onSelected,
      onMultiSelected,
    ],
  )

  const selectedDayFormatted = React.useMemo(
    () =>
      selectedDay
        ? moment(selectedDay.activityDayLocalTimeDate).format(DATE_FORMAT)
        : null,
    [selectedDay],
  )

  const renderCalendarBody = (
    daysData: DriverCalendarDayModel[][],
    height: number,
    width: number,
  ) => {
    return (
      <FlexView
        column
        hAlignContent="left"
        vAlignContent="center"
        style={{ border: 'solid', borderWidth: 0 }}
      >
        {daysData &&
          daysData.map((item, itemIndex) => {
            return (
              <FlexView
                key={itemIndex}
                height={height}
                hAlignContent="left"
                vAlignContent="center"
              >
                {item.map((day: DriverCalendarDayModel, dayIndex) => {
                  const dayDateFormatted = moment(
                    day.activityDayLocalTimeDate,
                  ).format(DATE_FORMAT)
                  const isSelectedDay =
                    selectedDayFormatted === dayDateFormatted
                  const isDayMultiSelected =
                    selectedDaysSet.has(dayDateFormatted)

                  return (
                    <CalendarDayItem
                    showTrips={showTrips}
                      driverId={driverId}
                      showViolations={showViolations}
                      companyId={companyId}
                      translate={t}
                      onCalendarAction={onCalendarAction}
                      onContextMenuRequest={handleContextMenuRequest}
                      isContextMenuOpen={contextMenu?.isOpen === true}
                      onSelected={handleDaySelection}
                      onMouseDown={handleDayMouseDown}
                      onMouseEnterDay={handleDayMouseEnter}
                      multiSelectMode={multiSelectMode}
                      isSelected={isSelectedDay}
                      isMultiSelected={isDayMultiSelected}
                      selectedOptions={selectedOptions}
                      key={dayIndex}
                      day={day}
                      height={height}
                      width={width}
                      refreshCalendar={refreshCalendar}
                      isFirstRow={itemIndex === 0}
                      isLastInRow={dayIndex === item.length - 1}
                    />
                  )
                })}
              </FlexView>
            )
          })}
      </FlexView>
    )
  }

  const hasDays = !!days && days.length > 0
  const numberOfColumns = days?.[0]?.length || CALENDAR_COLUMNS
  const WIDTH = getDayWidth(containerWidth, numberOfColumns)
  return (
    <div ref={calendarRef} className="tw-w-full">
      <div className="tw-flex tw-min-w-fit">
        {renderMonths(HEIGHT, MONTH_COLUMN_WIDTH)}
        <div className="tw-flex tw-flex-col">
          {renderWeekDayHeaders(WIDTH)}
          <div className="tw-relative">
            {isLoading ? (
              <CalendarCellsPlaceholder
                rows={CALENDAR_ROWS}
                columns={numberOfColumns}
                height={HEIGHT}
                width={WIDTH}
                shimmer
              />
            ) : hasDays ? (
              renderCalendarBody(days, HEIGHT, WIDTH)
            ) : (
              <>
                <CalendarCellsPlaceholder
                  rows={CALENDAR_ROWS}
                  columns={numberOfColumns}
                  height={HEIGHT}
                  width={WIDTH}
                />
                <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-grid tw-place-items-center tw-px-6">
                  <ActivitiesEmptyState
                    title={t('dailyActivities.empty-activities-title')}
                    subtitle={t('dailyActivities.empty-activities-subtitle')}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {popupDay &&
        popupAnchor &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: popupAnchor.top,
              left: popupAnchor.left,
              zIndex: 9999,
            }}
            className="tw-rounded-lg tw-shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarDayInfoPopup
              date={moment(popupDay.activityDayLocalTimeDate)}
              companyId={companyId}
              driverId={driverId}
              selectedDay={popupDay}
            />
          </div>,
          document.body,
        )}
      {isCertificateModalOpen &&
        createPortal(
          <CertificateModal selectedDates={certificateDates} companyId={companyId} driverId={driverId}  onClose={() => setIsCertificateModalOpen(false)} />,
          document.body,
        )}
      {contextMenu && (
        <DayContextMenu
        openCertificateModal={(dates) => {
          const resolved =
            dates && dates.length > 0
              ? dates
              : selectedDates.length > 0
                ? selectedDates
                : [contextMenu.day.activityDayLocalTimeDate]
          setCertificateDates(resolved)
          setIsCertificateModalOpen(true)
        }}
          key={String(contextMenu.day.activityDayLocalTimeDate)}
          driverId={driverId}
          companyId={companyId}
          day={contextMenu.day}
          translate={t}
          onCalendarAction={onCalendarAction}
          refreshCalendar={refreshCalendar}
          anchorPoint={contextMenu.anchor}
          isOpen={contextMenu.isOpen}
          onClose={handleContextMenuClose}
          selectedDays={selectedDays}
          currentSelectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
        />
      )}
    </div>
  )
}

export default YearlyActivityCalendar
